// frontend/src/components/layout/AppShell.js
//
// Hub360 application shell, modeled on joao-1wes/dashboard-static and the
// 1wescom/wes-style-guide brand system. Replaces the previous MUI-based
// dashboard layout: the sidebar, topbar, dropdowns and body chrome are
// plain HTML + CSS classes from `styles/dashboardShell.css` — MUI only
// survives inside the page content that <Outlet /> renders. See
// .cursor/rules/design.mdc and CLAUDE.md for the design decisions.
//
// Preserved from the old Dashboard shell:
//   - tenant-scoped routing via getTenantPath
//   - environment switcher (lives in the topbar user area)
//   - language switcher (same — reuses existing <LanguageSwitcher />)
//   - notifications dropdown with a placeholder count
//   - collapsible sidebar (state persisted in localStorage)
//   - ChatAssistant floating widget
//   - dark-mode toggle via ThemeContext

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useEntitlements } from '../../context/EntitlementsContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { ROLE, getRoleLabel, resolveCanonicalRoleName } from '../../constants/roles';
import { tenantNameToSlug } from '../../utils/tenantSlug';
import { logout } from '../../services/authService';
import { getDashboardStats } from '../../services/dashboardService';
import { useCanViewDashboard } from '../../hooks/useCanViewDashboard';
import { useIsHealthOnlyAccess } from '../../hooks/useIsHealthOnlyAccess';
import LanguageSwitcher from '../common/LanguageSwitcher';
import ThemeToggle from '../common/ThemeToggle';
import { ChatAssistant } from '../insights';
import { IdentitySessionProvider, firstNameOf, useIdentitySession } from '../../context/IdentitySessionContext';
import IdentityTopbarControls from '../identity/IdentityTopbarControls';
import SessionMask from '../identity/SessionMask';

import '../../styles/dashboardShell.css';
import '../../styles/appShell.css';
import '../../styles/appToast.css';
import '../../styles/designerComponents.css';
import '../../styles/modalOverrides.css';
import '../../styles/filterOverrides.css';
import '../../styles/errorRecovery.css';
import '../../styles/integrations.css';
import '../../styles/campaigns.css';
import '../../styles/idpPage.css';
import '../../styles/identity.css';
import { userAllowedSectionKeys } from '../../utils/sectionAccess';

const SIDEBAR_COLLAPSED_KEY = 'hub360:sidebar-collapsed';

// Tiny helper — the sidebar and dropdowns both rely on "click outside to
// close" behavior. React portals aren't needed; a document listener
// guarded by the element's ref is enough.
function useClickAway(handler) {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (event) => {
      if (!ref.current) return;
      if (ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [handler]);
  return ref;
}

// Material Symbols Rounded is already loaded by public/index.html.
function Icon({ name, className = '', ariaHidden = false }) {
  return (
    <span
      className={`material-symbols-rounded ${className}`.trim()}
      aria-hidden={ariaHidden || undefined}
    >
      {name}
    </span>
  );
}

function AppShellLayout() {
  const { t } = useTranslation();
  const { authenticated, user } = useAuth();
  const { isProductEnabled } = useEntitlements();
  // Same gate the home page uses (PBI #3968) — the topbar has to name what is
  // actually on screen, and role alone gets that wrong for a plain user whose
  // tenant granted `dashboard:read`.
  const canViewDashboard = useCanViewDashboard();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    environments,
    currentEnvironment,
    switchEnvironment,
    getCurrentEnvironmentName,
    canManageEnvironments,
  } = useEnvironment();

  // ---- state -------------------------------------------------------------
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState({});
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [envMenuOpen, setEnvMenuOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(3);
  const [counts, setCounts] = useState({});
  const displayName = user?.name || user?.full_name || user?.email || t('navigation.user', 'Usuário');
  const canonicalRoles = useMemo(
    () => [
      ...(user?.roles || []),
      ...(user?.platform_roles || []),
    ].map(resolveCanonicalRoleName),
    [user]
  );

  // Access profile shown under the user name (prototype `.user-tenant`). The
  // most privileged role wins so an admin_wes who also carries usuario_comum
  // does not read as a plain user.
  const profileLabel = useMemo(() => {
    const ordered = [ROLE.ADMIN_WES, ROLE.ADMIN_CLIENTE, ROLE.USUARIO_COMUM];
    const canon = ordered.find((role) => canonicalRoles.includes(role))
      || (user?.is_superuser ? ROLE.ADMIN_WES : null)
      || canonicalRoles[0];
    return canon ? getRoleLabel(canon, t) : t('navigation.user', 'Usuário');
  }, [canonicalRoles, user, t]);

  // Org + company spelled out, for the tooltip only.
  const contextTooltip = useMemo(() => {
    const parts = [];
    if (user?.organization_name) {
      parts.push(`${t('companies.contextChip.org')}: ${user.organization_name}`);
    }
    parts.push(
      `${t('companies.contextChip.company')}: ${user?.tenant_name || t('companies.contextChip.noCompany')}`
    );
    return parts.join(' · ');
  }, [user, t]);

  // ---- role helpers -------------------------------------------------------
  const isMenuAdmWes = useMemo(() => {
    return canonicalRoles.includes(ROLE.ADMIN_WES) || Boolean(user?.is_superuser);
  }, [canonicalRoles, user]);

  const isMenuAdmCliente = useMemo(() => {
    return canonicalRoles.includes(ROLE.ADMIN_CLIENTE);
  }, [canonicalRoles]);

  // An account whose only permissions are health_care:* (whatever the name
  // of its função) never sees platform-infra menu groups (Automação,
  // Infraestrutura, Atendimento Dinâmico, etc.) — those are unconditionally
  // in baseMenu below for every other profile, so this is an allowlist
  // applied after the fact rather than touching each group's own gate.
  const isHealthOnlyAccess = useIsHealthOnlyAccess();
  // By group `id`, not `sectionKey` — `agents` (Agentes IA) shares the
  // 'dashboard' sectionKey with `overview` (Painel) but must stay hidden.
  const HEALTH_ONLY_GROUP_IDS = useMemo(() => new Set(['overview', 'health']), []);

  // Mirrors what ProtectedRoute grants: a route declaring requiredRoles AND
  // requiredPermissions is satisfied by *either*. Gating a menu entry on the
  // roles alone hides a page the user is allowed to open.
  const hasPermission = useCallback(
    (permission) => (user?.permissions || []).includes(permission),
    [user]
  );

  // Mesma regra do guarda de rota: `null` e `[]` sao estados diferentes. Ver
  // o cabecalho de utils/sectionAccess.js.
  const allowedSectionKeys = useMemo(() => userAllowedSectionKeys(user), [user]);

  const canAccessSection = useCallback(
    (sectionKey) => {
      if (!sectionKey) return true;
      if (allowedSectionKeys === null) return true;
      return allowedSectionKeys.has(sectionKey);
    },
    [allowedSectionKeys]
  );

  // Apply `body.dashboard-shell` while the shell is mounted so the scoped
  // CSS in dashboardShell.css actually applies. Undone on unmount.
  //
  // `useLayoutEffect`, não `useEffect`: efeito passivo roda **depois** da
  // pintura, então havia um quadro em que o shell inteiro aparecia sem
  // nenhuma das regras deste arquivo — markup cru. O efeito de layout roda
  // na fase de commit, antes de o navegador pintar, e fecha essa janela.
  useLayoutEffect(() => {
    document.body.classList.add('dashboard-shell');
    return () => document.body.classList.remove('dashboard-shell');
  }, []);

  // Persist collapsed state across sessions.
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);

  // Poll dashboard counts every 30s (mirror of the previous shell behavior).
  // Guards on auth to avoid 401 noise. Re-pull when tenant changes so the
  // sidebar counts match the active JWT (select-tenant, Fast Refresh, etc.).
  const tenantScopeKey =
    user?.tenant_id != null && user.tenant_id !== ''
      ? String(user.tenant_id)
      : user?.tenant_name || '';

  useEffect(() => {
    if (!authenticated || !tenantScopeKey) return undefined;
    let cancelled = false;
    const pull = async () => {
      try {
        const stats = await getDashboardStats();
        if (cancelled) return;
        setCounts({
          automations: stats.jobs?.total || 0,
          schedules: stats.schedules?.total || 0,
          queues: 0,
          executors: stats.agents?.total || 0,
          packages: stats.packages?.total || 0,
        });
      } catch {
        // 401s on startup are expected; ignore and let ProtectedRoute handle
      }
    };
    pull();
    const interval = setInterval(pull, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [authenticated, tenantScopeKey]);

  // ---- nav model ---------------------------------------------------------
  const { identityAvailable, hasStationSession, sensitiveActionsBlocked } = useIdentitySession();
  // Inicial do avatar sem o tratamento: "Dra. Helena" é "H", não "D".
  const avatarInitial = (firstNameOf(displayName) || displayName).slice(0, 1).toUpperCase();
  // Selo do WES ID: a identidade foi confirmada nesta estação, pelo rosto ou
  // pelo PIN (a entrada por PIN é a que limita as ações sensíveis).
  const verifiedLabel = hasStationSession
    ? t(sensitiveActionsBlocked ? 'identity.verified.pin' : 'identity.verified.face')
    : null;
  const menuGroups = useMemo(() => {
    // Hide any group/item whose product entitlement is explicitly disabled
    // (task #3782). Fail-open: an unset entitlement stays visible.
    const isEnabled = (entitlement) => !entitlement || isProductEnabled(entitlement);

    const baseMenu = [
      {
        id: 'overview',
        sectionKey: 'dashboard',
        icon: 'grid_view',
        label: t('navigation.dashboard'),
        path: '/dashboard',
      },
      {
        id: 'agents',
        sectionKey: 'dashboard',
        icon: 'smart_toy',
        label: t('navigation.agents'),
        path: '/dashboard/ai-agents',
      },
      // Same gate as the route: admin bypass OR agent:read. Sem `sectionKey`
      // de proposito: monitorar as proprias VMs entra no pacote basico, entao o
      // item nao depende mais de a secao Infraestrutura estar contratada
      // (decisao de Produto 11, 15/09/2026 -- comercial, nao tecnica).
      ...((isMenuAdmCliente || isMenuAdmWes || hasPermission('agent:read'))
        ? [{
            id: 'vm-monitoring',
            icon: 'desktop_windows',
            label: t('navigation.vmMonitoring'),
            path: '/dashboard/vm-monitoring',
          }]
        : []),
      {
        id: 'automation',
        sectionKey: 'automation',
        icon: 'automation',
        label: t('navigation.workflows'),
        items: [
          { label: t('navigation.automations'), path: '/dashboard/automations', count: counts.automations },
          {
            label: t('navigation.idp'),
            path: '/dashboard/automation/idp',
            entitlement: 'document_analysis',
          },
          // Mineracao de processos era item de primeiro nivel; no prototipo e
          // submenu daqui. O gate de permissao veio junto: solto, o item
          // apareceria para quem nao pode abrir a pagina.
          ...((isMenuAdmCliente || isMenuAdmWes || hasPermission('process_mining:read'))
            ? [{ label: t('navigation.processMining'), path: '/dashboard/bpmn' }]
            : []),
        ],
      },
      // Grupo "Infraestrutura" (Executores + Pacotes) fica fora do menu: o
      // protótipo o mantém comentado no HTML, para todos os perfis. As rotas
      // /dashboard/executors e /dashboard/packages continuam existindo e
      // acessíveis por URL ou link direto — só a entrada do menu saiu.
      // PBI #4552, D0.4.
      {
        id: 'dynamic-service',
        sectionKey: 'integrations',
        icon: 'account_tree',
        label: t('navigation.dynamicService'),
        labelClassName: 'nav-label--two-lines',
        items: [
          { label: t('navigation.campaigns'), path: '/dashboard/campaigns' },
          { label: t('navigation.integrationsChannels'), path: '/dashboard/integrations' },
          { label: t('navigation.hybridFlows'), path: '/dashboard/automation/hybrid-flows' },
          {
            label: t('navigation.voiceMessaging'),
            path: '/dashboard/voice-messaging',
            entitlement: 'voice_agent',
          },
        ],
      },
      // Saúde section (PBI #3980). Same gate as the route: admin bypass OR
      // health_care:read; the `health` sectionKey still runs through
      // canAccessSection so a tenant that never granted it keeps it hidden.
      ...((isMenuAdmCliente || isMenuAdmWes || hasPermission('health_care:read'))
        ? [{
            id: 'health',
            sectionKey: 'health',
            icon: 'health_and_safety',
            label: t('navigation.health'),
            // Ordem do prototipo. "Agente do paciente" e "Agenda" ficam de
            // fora: o prototipo os tem, o Hub nao tem as paginas.
            items: [
              { label: t('navigation.healthOverview'), path: '/dashboard/health/overview' },
              { label: t('navigation.healthPatientAgent'), path: '/dashboard/health/whatsapp' },
              { label: t('navigation.healthService'), path: '/dashboard/health-care' },
              { label: t('navigation.healthAgenda'), path: '/dashboard/health/agenda' },
              {
                label: t('navigation.healthAccountability'),
                path: '/dashboard/health-care/accountability',
              },
              {
                label: t('navigation.healthExamReading'),
                path: '/dashboard/health-care/exam-reading',
              },
            ],
          }]
        : []),
    ];

    // Ordem do prototipo: Auditoria, Conexoes, Estrutura e Pessoas, Habilidades.
    // As duas paginas de auditoria do Hub viraram uma so — `AuditPage` foi
    // aposentada e /dashboard/audit redireciona para /dashboard/ai-audit
    // (App.js) —, entao aqui e um item unico, "Auditoria", como no prototipo.
    // O gate `audit_logs` segue valendo: overlay que desliga auditoria nao
    // mostra o item (PBI #4552).
    const adminItems = [
      {
        label: t('navigation.aiAudit'),
        path: '/dashboard/ai-audit',
        entitlement: 'audit_logs',
      },
      { label: t('navigation.mcps'), path: '/dashboard/mcps' },
      ...((isMenuAdmWes || isMenuAdmCliente)
        ? [{
            label: t('navigation.structureAndPeople'),
            path: '/dashboard/structure-and-people',
          }]
        : []),
      { label: t('navigation.skills'), path: '/dashboard/skills' },
      // WES Identity: só onde a POC está acesa para o tenant.
      ...(identityAvailable
        ? [
          { label: t('identity.admin.menu.workstations'), path: '/dashboard/workstations' },
          { label: t('identity.admin.menu.history'), path: '/dashboard/access-history' },
        ]
        : []),
    ];

    const rawGroups = [
      ...baseMenu,
      ...((isMenuAdmWes || isMenuAdmCliente)
        ? [{
            id: 'admin',
            sectionKey: 'administration',
            icon: 'admin_panel_settings',
            label: t('navigation.administration'),
            items: adminItems,
          }]
        : []),
    ];

    return rawGroups
      .filter((group) => canAccessSection(group.sectionKey))
      .filter((group) => !isHealthOnlyAccess || HEALTH_ONLY_GROUP_IDS.has(group.id))
      .filter((group) => isEnabled(group.entitlement))
      .map((group) =>
        group.items
          ? { ...group, items: group.items.filter((item) => isEnabled(item.entitlement)) }
          : group,
      )
      .filter((group) => group.path || (group.items && group.items.length > 0));
  }, [
    t,
    counts,
    identityAvailable,
    isMenuAdmCliente,
    isMenuAdmWes,
    isHealthOnlyAccess,
    HEALTH_ONLY_GROUP_IDS,
    hasPermission,
    canAccessSection,
    isProductEnabled,
  ]);


  // ---- routing helpers --------------------------------------------------
  const getTenantPath = useCallback((path) => {
    if (!user || !user.tenant_name) return path;
    const slug = tenantNameToSlug(user.tenant_name);
    const clean = path.replace('/dashboard/', '').replace('/dashboard', '');
    return `/${slug}/${clean}`;
  }, [user]);

  const isActiveRoute = useCallback((path) => {
    if (!path) return false;
    const tenantAware = getTenantPath(path);
    if (location.pathname === tenantAware) return true;
    // "/" path is only active at exactly the root to avoid always-on highlight
    if (tenantAware !== '/' && location.pathname.startsWith(tenantAware + '/')) return true;
    return false;
  }, [location.pathname, getTenantPath]);

  const isActiveGroup = useCallback((group) => {
    if (!group.items) return false;
    return group.items.some((item) => isActiveRoute(item.path));
  }, [isActiveRoute]);

  /**
   * Which single submenu item may carry `.active`.
   *
   * `isActiveRoute` matches on prefix so a detail route keeps its parent lit
   * (`health-care/encounters/:id` → "Atendimento"). When one item's path nests
   * under another's — `health-care` and `health-care/exam-reading` — that lights
   * up both, and the prototype only ever marks one. The deepest match wins;
   * groups with a single match are unaffected.
   */
  const activeItemPath = useCallback((group) => {
    const matches = (group.items || []).filter((item) => isActiveRoute(item.path));
    if (!matches.length) return null;
    return matches.reduce(
      (deepest, item) => (item.path.length > deepest.path.length ? item : deepest),
    ).path;
  }, [isActiveRoute]);

  const activeGroupId = useMemo(() => {
    const activeGroup = menuGroups.find((group) => (
      group.items && group.items.some((item) => isActiveRoute(item.path))
    ));
    return activeGroup?.id || null;
  }, [menuGroups, isActiveRoute]);

  useEffect(() => {
    if (collapsed) return;
    setExpandedMenu((prev) => {
      if (!activeGroupId) {
        return Object.keys(prev).length === 0 ? prev : {};
      }
      if (prev[activeGroupId] && Object.keys(prev).length === 1) {
        return prev;
      }
      return { [activeGroupId]: true };
    });
  }, [activeGroupId, collapsed]);

  const goTo = useCallback((path) => {
    navigate(getTenantPath(path));
    setMobileOpen(false);
    setNotificationsOpen(false);
    setUserMenuOpen(false);
    setEnvMenuOpen(false);
  }, [navigate, getTenantPath]);

  const toggleGroup = useCallback((groupId) => {
    if (collapsed) {
      // When collapsed, first click expands the sidebar AND opens the group.
      setCollapsed(false);
      setExpandedMenu({ [groupId]: true });
      return;
    }
    // Accordion behavior: opening a group collapses any other open one; a
    // second click on the same group closes it.
    setExpandedMenu((prev) => (prev[groupId] ? {} : { [groupId]: true }));
  }, [collapsed]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      if (next) setExpandedMenu({});
      return next;
    });
  }, []);

  // ---- topbar title mapping ---------------------------------------------
  // When the user is on a leaf page (e.g. /dashboard/automations), show the
  // parent group label ("Automação") so the topbar always reflects where
  // the sidebar highlight sits, not the specific submenu item. Top-level
  // entries (like /dashboard itself) keep their own label.
  const pageTitle = useMemo(() => {
    // Without `dashboard:read` the tenant root renders the profile Home
    // (DashboardCommonHome); the sidebar entry stays "Painel" but the topbar
    // names what is actually on screen.
    const pathParts = location.pathname.split('/').filter(Boolean);
    const isDashboardHome =
      pathParts.length === 0 ||
      pathParts.length === 1 ||
      (pathParts.length === 2 && pathParts[0] === 'dashboard' && !pathParts[1]);
    if (!canViewDashboard && isDashboardHome) {
      return t('navigation.home');
    }
    for (const group of menuGroups) {
      if (group.path && isActiveRoute(group.path)) return group.label;
      if (group.items && group.items.some((item) => isActiveRoute(item.path))) {
        return group.label;
      }
    }
    return t('navigation.dashboard');
  }, [menuGroups, isActiveRoute, t, canViewDashboard, location.pathname]);

  // ---- dropdown refs (click-away) ---------------------------------------
  const notifRef = useClickAway(() => setNotificationsOpen(false));
  const userRef = useClickAway(() => setUserMenuOpen(false));
  const envRef = useClickAway(() => setEnvMenuOpen(false));

  // ---- handlers ---------------------------------------------------------
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchEnv = async (envId) => {
    try {
      await switchEnvironment(envId);
      setEnvMenuOpen(false);
    } catch (err) {
      console.error('Failed to switch environment:', err);
    }
  };

  const handleOpenNotifications = () => {
    setNotificationsOpen((v) => !v);
    setUserMenuOpen(false);
    setEnvMenuOpen(false);
    // Opening the panel resets the unread pill, like the previous shell did.
    if (!notificationsOpen) setNotificationCount(0);
  };

  // ---- render ------------------------------------------------------------
  return (
    <div className={`app-shell${collapsed ? ' sidebar-collapsed' : ''}${mobileOpen ? ' sidebar-mobile-open' : ''}`} id="appShell">
      <aside className="sidebar" aria-label={t('navigation.mainNavigation')} data-cy="app-sidebar">
        <div className="sidebar-top">
          <button
            type="button"
            className="icon-btn menu-btn"
            onClick={toggleCollapsed}
            aria-label={collapsed ? t('navigation.expandSidebar', 'Expandir menu') : t('navigation.collapseSidebar', 'Recolher menu')}
            aria-expanded={!collapsed}
          >
            <Icon name="menu" />
          </button>
        </div>

        <div className="logo">
          <img
            className="logo-big"
            src={`${process.env.PUBLIC_URL}/images/logo_big.svg`}
            alt="WES Hub 360"
          />
          <img
            className="logo-small"
            src={`${process.env.PUBLIC_URL}/images/logo_small.svg`}
            alt=""
          />
        </div>

        <nav className="nav" id="mainNavigation">
          {menuGroups.map((group) => (
            group.items ? (
              <div
                key={group.id}
                className={`nav-group${expandedMenu[group.id] ? ' open' : ''}${isActiveGroup(group) ? ' active' : ''}`}
              >
                <button
                  type="button"
                  className={`nav-item nav-trigger${isActiveGroup(group) ? ' active' : ''}${expandedMenu[group.id] ? ' open' : ''}`}
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={!!expandedMenu[group.id]}
                  aria-controls={`submenu-${group.id}`}
                  title={collapsed ? group.label : undefined}
                  data-cy={`nav-group-${group.id}`}
                >
                  <Icon name={group.icon} />
                  <span className={`nav-label${group.labelClassName ? ` ${group.labelClassName}` : ''}`}>{group.label}</span>
                  <span className="nav-caret" aria-hidden>▾</span>
                </button>
                <div
                  className={`submenu${expandedMenu[group.id] ? ' open' : ''}`}
                  id={`submenu-${group.id}`}
                >
                  {group.items.map((item) => (
                    <button
                      key={item.path}
                      type="button"
                      className={`submenu-item${activeItemPath(group) === item.path ? ' active' : ''}`}
                      onClick={() => goTo(item.path)}
                      data-cy={`nav-item-${item.path.split('/').filter(Boolean).pop()}`}
                    >
                      <span className="submenu-label">{item.label}</span>
                      {item.count ? <span className="submenu-count">{item.count}</span> : null}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button
                key={group.id}
                type="button"
                className={`nav-item${isActiveRoute(group.path) ? ' active' : ''}`}
                onClick={() => goTo(group.path)}
                title={collapsed ? group.label : undefined}
                data-cy={`nav-item-${group.id}`}
              >
                <Icon name={group.icon} />
                <span className="nav-label">{group.label}</span>
              </button>
            )
          ))}

          <div className="nav-divider" />
        </nav>

      </aside>

      {/* Mobile backdrop — tapping it closes the drawer */}
      <div
        className="sidebar-backdrop"
        role="presentation"
        onClick={() => setMobileOpen(false)}
        hidden={!mobileOpen}
      />

      <main className="main">
        <header className="topbar">
          <div className="topbar-left">
            <button
              type="button"
              className="icon-btn topbar-mobile-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={t('navigation.openMenu', 'Abrir menu')}
            >
              <Icon name="menu" />
            </button>
            <h1 className="topbar-title">{pageTitle}</h1>
          </div>

          <div className="topbar-actions">
            <LanguageSwitcher variant="button" />
            <ThemeToggle variant="topbar" />
            <IdentityTopbarControls />

            {environments && environments.length > 1 && (
              <div className="environment-switcher" ref={envRef}>
                <button
                  type="button"
                  className="icon-btn environment-btn"
                  onClick={() => { setEnvMenuOpen((v) => !v); setNotificationsOpen(false); setUserMenuOpen(false); }}
                  aria-haspopup="menu"
                  aria-expanded={envMenuOpen}
                  title={getCurrentEnvironmentName?.() || ''}
                >
                  <Icon name="workspaces" />
                  <span className="environment-name">{getCurrentEnvironmentName?.() || t('environment.select', 'Ambiente')}</span>
                </button>
                <div className={`dropdown environment-dropdown${envMenuOpen ? ' open' : ''}`} role="menu">
                  <div className="dropdown-header">
                    {t('environment.switchTitle', 'Alternar ambiente')}
                  </div>
                  <ul className="dropdown-list">
                    {environments.map((env) => (
                      <li key={env.id}>
                        <button
                          type="button"
                          role="menuitem"
                          className={`dropdown-item${currentEnvironment?.id === env.id ? ' active' : ''}`}
                          onClick={() => handleSwitchEnv(env.id)}
                        >
                          <span>{env.name}</span>
                          {currentEnvironment?.id === env.id && <Icon name="check" />}
                        </button>
                      </li>
                    ))}
                  </ul>
                  {canManageEnvironments && (
                    <div className="dropdown-footer">
                      <button type="button" className="dropdown-item" onClick={() => goTo('/dashboard/environments')}>
                        <Icon name="settings" />
                        <span>{t('environment.manage', 'Gerenciar ambientes')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="notifications" ref={notifRef}>
              <button
                type="button"
                className="icon-btn notifications-btn"
                onClick={handleOpenNotifications}
                aria-haspopup="dialog"
                aria-expanded={notificationsOpen}
                aria-label={t('navigation.notifications')}
              >
                <Icon name="notifications" />
                {notificationCount > 0 && <span className="notifications-badge">{notificationCount}</span>}
              </button>
              <div className={`dropdown notifications-dropdown${notificationsOpen ? ' open' : ''}`} role="dialog">
                <div className="notifications-header">
                  <span>{t('navigation.notifications')}</span>
                  <button type="button" className="link-btn" onClick={() => setNotificationCount(0)}>
                    {t('notifications.markAllAsRead')}
                  </button>
                </div>
                <ul className="notifications-list">
                  <li>
                    <p className="notification-title">{t('notifications.jobCompleted')}</p>
                    <p className="notification-meta">{t('notifications.dailyReport')}</p>
                  </li>
                  <li>
                    <p className="notification-title">{t('notifications.newExecutor')}</p>
                    <p className="notification-meta">{t('notifications.executorOnline')}</p>
                  </li>
                  <li>
                    <p className="notification-title">{t('notifications.packageUploaded')}</p>
                  </li>
                </ul>
              </div>
            </div>

            <div className="user-menu" ref={userRef}>
              <button
                type="button"
                className="user-menu-trigger"
                onClick={() => { setUserMenuOpen((v) => !v); setNotificationsOpen(false); setEnvMenuOpen(false); }}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                aria-label={verifiedLabel ? `${displayName}, ${verifiedLabel}` : displayName}
                data-cy="user-menu-trigger"
              >
                <span className="user-avatar-wrap">
                  <span className="user-avatar" aria-hidden>
                    {avatarInitial}
                  </span>
                  {verifiedLabel ? (
                    <span
                      className="user-verified-badge"
                      id="userVerifiedBadge"
                      role="img"
                      aria-label={verifiedLabel}
                      data-tooltip={verifiedLabel}
                    >
                      <span className="material-symbols-rounded" aria-hidden="true">verified</span>
                    </span>
                  ) : null}
                </span>
                <span className="user-meta">
                  <span className="user-name">{displayName}</span>
                  {/* Prototype `#userMenu` shape: the access profile as plain
                    * text plus the org/company chips. Chips render whole (PBI
                    * #4948); only the profile label shrinks, so the tooltip
                    * keeps the full pair for when the topbar gets tight. */}
                  <span
                    className="user-context-line"
                    aria-label={t('companies.contextChip.aria')}
                    title={contextTooltip}
                  >
                    <span className="user-tenant">{profileLabel}</span>
                    {/* Organização e empresa com o mesmo nome (tenant único) viravam
                      * dois chips iguais, "wes-hub-360 wes-hub-360". */}
                    {user?.organization_name && user.organization_name !== user?.tenant_name ? (
                      <span
                        className="user-company-chip"
                        aria-label={t('companies.contextChip.orgSelected', {
                          name: user.organization_name,
                        })}
                      >
                        {user.organization_name}
                      </span>
                    ) : null}
                    <span
                      className="user-company-chip"
                      aria-label={
                        user?.tenant_name
                          ? t('companies.contextChip.companySelected', { name: user.tenant_name })
                          : t('companies.contextChip.companyNotSelected')
                      }
                    >
                      {user?.tenant_name || t('companies.contextChip.noCompany')}
                    </span>
                  </span>
                </span>
                <Icon name="expand_more" className="user-caret" ariaHidden />
              </button>
              <div className={`dropdown user-dropdown${userMenuOpen ? ' open' : ''}`} role="menu">
                <button type="button" role="menuitem" className="dropdown-item" onClick={() => goTo('/dashboard/profile')} data-cy="user-menu-profile">
                  <Icon name="person" />
                  <span>{t('navigation.profile')}</span>
                </button>
                {isMenuAdmWes ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="dropdown-item"
                    onClick={() => {
                      navigate('/select-tenant?from=app');
                      setUserMenuOpen(false);
                      setNotificationsOpen(false);
                      setEnvMenuOpen(false);
                      setMobileOpen(false);
                    }}
                    data-cy="switch-organization-btn"
                  >
                    <Icon name="business" />
                    <span>{t('navigation.switchOrganization')}</span>
                  </button>
                ) : null}
                {(isMenuAdmWes || isMenuAdmCliente) && (
                  <button
                    type="button"
                    role="menuitem"
                    className="dropdown-item"
                    onClick={() => {
                      const orgId = user?.organization_id;
                      navigate(
                        orgId
                          ? `/select-tenant?step=company&from=app&organization_id=${encodeURIComponent(orgId)}`
                          : '/select-tenant?step=company&from=app'
                      );
                      setUserMenuOpen(false);
                      setNotificationsOpen(false);
                      setEnvMenuOpen(false);
                      setMobileOpen(false);
                    }}
                    data-cy="switch-company-btn"
                  >
                    <Icon name="store" />
                    <span>{t('navigation.switchCompany', 'Alternar empresa')}</span>
                  </button>
                )}
                <button type="button" role="menuitem" className="dropdown-item" onClick={() => goTo('/dashboard/settings')} data-cy="user-menu-settings">
                  <Icon name="settings" />
                  <span>{t('navigation.settings')}</span>
                </button>
                <div className="dropdown-divider" />
                <button type="button" role="menuitem" className="dropdown-item danger" onClick={handleLogout} data-cy="user-menu-logout">
                  <Icon name="logout" />
                  <span>{t('navigation.logout')}</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <section className="content">
          <Outlet />
        </section>
      </main>

      {/* floating=true renders the FAB + toggleable drawer. Default
       * floating=false keeps the panel permanently open. */}
      <ChatAssistant floating />
    </div>
  );
}

/**
 * O shell dentro da sessão do WES Identity. A máscara fica fora do
 * `.app-shell` de propósito: cobre o shell inteiro, inclusive a barra
 * lateral e o assistente. Sem sessão de estação (a maior parte do Hub), o
 * provider não renderiza nada além do shell.
 */
export default function AppShell() {
  return (
    <IdentitySessionProvider>
      <AppShellLayout />
      <SessionMask />
    </IdentitySessionProvider>
  );
}
